/**
 * Activity (event) Overview cards for the MSR portal (business lead, 2026-10-27):
 *  - ActivityVerifySaleCard: "they open up the activity, they verify their members" -- the same email box as the
 *    Verify a Sale tab; here the sale also counts toward this activity's close rate.
 *  - ManualCountsCard: Reinstatements and Upgrades, typed in as numbers next to Attendance, per day, per rep. Shown
 *    separately and NOT part of close rate (owner decision). Temporary until Zuora can verify them.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import VerifySaleForm from "./VerifySaleForm";
import { useToast } from "../ui/Toast";
import {
  centralToday,
  getActivityDates,
  getManualCounts,
  getMyVerifiedSales,
  msrErrorText,
  setManualCounts,
} from "../../services/msrFeedback.service";
import { dayLabel } from "../../services/timeOffConflicts";

export const ActivityVerifySaleCard: React.FC<{ eventId: string }> = ({
  eventId,
}) => {
  const { data: mine = [] } = useQuery({
    queryKey: ["msr-my-verified-sales", eventId],
    queryFn: () => getMyVerifiedSales(eventId),
    retry: false,
  });
  return (
    <div
      className="space-y-3 rounded-lg border border-gray-200 bg-white p-4"
      data-testid="msr-activity-verify-sale"
    >
      <h2 className="text-base font-semibold text-gray-900">Verify a sale</h2>
      <p className="text-sm text-gray-600">
        Find a member you signed up here by name, email, phone or promo code.
        Verified sales count toward this activity's close rate.
      </p>
      <VerifySaleForm eventId={eventId} />
      {mine.length > 0 && (
        <div className="text-sm text-gray-700">
          <div className="font-medium">You verified {mine.length} here:</div>
          <ul className="mt-1 space-y-0.5 text-gray-600">
            {mine.map((s) => (
              <li key={s.id} className="truncate">
                {s.member_email ?? "Verified by name, phone or promo code"}
                {s.service_date ? ` · ${dayLabel(s.service_date)}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

function pickDefaultDay(dates: string[], today: string): string {
  if (dates.includes(today)) {
    return today;
  }
  const past = dates.filter((d) => d <= today);
  return past.length ? past[past.length - 1] : (dates[0] ?? "");
}

export const ManualCountsCard: React.FC<{ eventId: string }> = ({
  eventId,
}) => {
  const { showToast } = useToast();
  const qc = useQueryClient();
  const today = useMemo(() => centralToday(), []);
  const { data: dates = [] } = useQuery({
    queryKey: ["msr-activity-dates", eventId],
    queryFn: () => getActivityDates(eventId),
    retry: false,
  });
  const { data: rows = [], error } = useQuery({
    queryKey: ["msr-manual-counts", eventId],
    queryFn: () => getManualCounts(eventId),
    retry: false,
  });
  const [day, setDay] = useState("");
  useEffect(() => {
    if (!day && dates.length) {
      setDay(pickDefaultDay(dates, today));
    }
  }, [dates, day, today]);
  const mineForDay = rows.find((r) => r.is_mine && r.service_date === day);
  const [reinst, setReinst] = useState("0");
  const [upg, setUpg] = useState("0");
  useEffect(() => {
    setReinst(String(mineForDay?.reinstatements ?? 0));
    setUpg(String(mineForDay?.upgrades ?? 0));
  }, [mineForDay?.reinstatements, mineForDay?.upgrades, day]);

  const mut = useMutation({
    mutationFn: () =>
      setManualCounts(eventId, day, Number(reinst), Number(upg)),
    onSuccess: () => {
      showToast("success", "Saved");
      qc.invalidateQueries({ queryKey: ["msr-manual-counts", eventId] });
      qc.invalidateQueries({ queryKey: ["msr-sale-totals"] });
    },
    onError: (e) => showToast("error", msrErrorText(e, "Could not save")),
  });

  if (error) {
    return null; // not on this activity's Sales Reps list: nothing to enter here
  }
  const ok = (v: string) => /^\d{1,4}$/.test(v) && Number(v) <= 1000;
  const changed =
    String(mineForDay?.reinstatements ?? 0) !== reinst ||
    String(mineForDay?.upgrades ?? 0) !== upg;

  return (
    <div
      className="space-y-3 rounded-lg border border-gray-200 bg-white p-4"
      data-testid="msr-manual-counts"
    >
      <h2 className="text-base font-semibold text-gray-900">
        Reinstatements &amp; upgrades
      </h2>
      <p className="text-xs text-gray-500">
        Type how many you did. Your manager confirms them for now. They are not
        part of close rate.
      </p>
      {dates.length > 1 && (
        <select
          value={day}
          onChange={(e) => setDay(e.target.value)}
          className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
          aria-label="Day"
        >
          {dates.map((d) => (
            <option key={d} value={d}>
              {dayLabel(d)}
            </option>
          ))}
        </select>
      )}
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm text-gray-800">
          Reinstatements
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={1000}
            value={reinst}
            onChange={(e) => setReinst(e.target.value)}
            className="mt-1 min-h-[44px] w-full rounded-md border border-gray-300 px-3 text-base"
            data-testid="msr-reinstatements"
          />
        </label>
        <label className="block text-sm text-gray-800">
          Upgrades
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={1000}
            value={upg}
            onChange={(e) => setUpg(e.target.value)}
            className="mt-1 min-h-[44px] w-full rounded-md border border-gray-300 px-3 text-base"
            data-testid="msr-upgrades"
          />
        </label>
      </div>
      <button
        type="button"
        disabled={!day || !ok(reinst) || !ok(upg) || !changed || mut.isPending}
        onClick={() => mut.mutate()}
        className="w-full min-h-[44px] rounded-md bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {mut.isPending ? "Saving…" : "Save numbers"}
      </button>
    </div>
  );
};
